import React, { useEffect, useState } from 'react'

import * as styles from './hotel-card.module.css'

const logoMap = {
  rakuten: '/images/rakuten-travel.jpeg',
  jalan: '/images/jalan.png',
  yahoo: '/images/yahoo_travel.png',
  ikyu: '/images/ikyu.png',
}

const labelMap = {
  rakuten: '楽天トラベル',
  jalan: 'じゃらん',
  yahoo: 'Yahoo!トラベル',
  ikyu: '一休.com',
}

const badgeLabelMap = {
  rakuten: '楽天',
  jalan: 'じゃらん',
  yahoo: 'Yahoo!',
  ikyu: '一休',
}

const linkOrder = ['rakuten', 'jalan', 'yahoo', 'ikyu']

const rakutenPriceCache = {}
const priceFormatter = new Intl.NumberFormat('ja-JP')

const buildRakutenAffiliateUrl = (destinationUrl, affiliateSourceUrl) => {
  if (!destinationUrl) return ''
  if (/^https:\/\/hb\.afl\.rakuten\.co\.jp\/hgc\//i.test(destinationUrl)) {
    return destinationUrl
  }

  const affiliateId = String(affiliateSourceUrl || '').match(
    /^https:\/\/hb\.afl\.rakuten\.co\.jp\/hgc\/([A-Za-z0-9._-]+)\//i
  )?.[1]
  if (!affiliateId) return destinationUrl

  const encodedUrl = encodeURIComponent(destinationUrl)
  return `https://hb.afl.rakuten.co.jp/hgc/${affiliateId}/?pc=${encodedUrl}&m=${encodedUrl}`
}

const extractRakutenHotelNo = (link) => {
  if (!link) return null
  if (link.hotelNo) return String(link.hotelNo)

  const candidates = [link.url, link.couponUrl].filter(Boolean)

  for (const candidate of candidates) {
    const decoded = decodeURIComponent(candidate)
    const match = decoded.match(/(?:HOTEL|hotel|coupon\/hotel)\/(\d+)/)
    if (match) return match[1]
  }

  return null
}

const useRakutenPrice = (linkKey, link) => {
  const hotelNo = linkKey === 'rakuten' ? extractRakutenHotelNo(link) : null
  const [price, setPrice] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!hotelNo) return undefined

    let active = true

    if (rakutenPriceCache[hotelNo]) {
      setPrice(rakutenPriceCache[hotelNo])
      return undefined
    }

    setLoading(true)

    fetch('/api/rakuten-hotel-price?hotelNo=' + encodeURIComponent(hotelNo))
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!active || !data || !data.price) return
        rakutenPriceCache[hotelNo] = String(data.price)
        setPrice(String(data.price))
      })
      .catch(() => {})
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [hotelNo])

  return { price, loading }
}

const formatPrice = (price) => priceFormatter.format(Number(String(price).replace(/,/g, '')))

const ExternalLink = ({ href, className, children }) => {
  if (!href) return null

  return (
    <a
      className={className}
      href={href}
      target="_blank"
      rel="nofollow sponsored noopener noreferrer"
    >
      {children}
    </a>
  )
}

const HotelLink = ({ linkKey, link }) => {
  const serviceClass = styles[linkKey + 'Link'] || ''
  const dynamic = useRakutenPrice(linkKey, link)
  const displayPrice = dynamic.price || link.price
  const couponUrl =
    linkKey === 'rakuten'
      ? buildRakutenAffiliateUrl(link.couponUrl, link.url)
      : link.couponUrl

  return (
    <div className={styles.linkGroup}>
      <ExternalLink href={link.url} className={[styles.hotelLink, serviceClass].join(' ')}>
        <span className={styles.serviceBadge}>{badgeLabelMap[linkKey]}</span>
        <span className={styles.bannerContainer}>
          <span className={styles.banner}>
            <img src={logoMap[linkKey]} alt={labelMap[linkKey]} loading="lazy" decoding="async" />
            <span className={styles.viewText}>で見る</span>
          </span>
          {displayPrice ? (
            <span className={styles.bannerPriceText}>
              最安値 <strong className={styles.bannerPrice}>{formatPrice(displayPrice)}</strong> 円〜
            </span>
          ) : (
            <span className={styles.bannerPriceText}>
              {dynamic.loading ? '料金確認中' : '空室・プランを確認'}
            </span>
          )}
        </span>
        <span className={styles.ctaText}>空室・料金を見る</span>
      </ExternalLink>
      <ExternalLink href={couponUrl} className={styles.couponLink}>
        お得な<span>クーポン</span>をチェック
      </ExternalLink>
    </div>
  )
}

const HotelCard = ({ hotel }) => {
  if (!hotel) return null

  const links = hotel.links || {}

  return (
    <section className={styles.card} aria-labelledby={'hotel-' + hotel.name}>
      <h2 id={'hotel-' + hotel.name} className={styles.title}>
        {hotel.name}
      </h2>

      <div className={styles.summary}>
        {hotel.image && (
          <div className={styles.mainImageWrap}>
            <img
              className={styles.mainImage}
              src={hotel.image}
              alt={hotel.name}
              width="250"
              height="250"
              loading="lazy"
              decoding="async"
            />
          </div>
        )}
        <div className={styles.copyBlock}>
          {hotel.catchcopy && <div className={styles.catchcopy}>{hotel.catchcopy}</div>}
          {hotel.comment && <div className={styles.comment}>{hotel.comment}</div>}
        </div>
      </div>

      <div className={styles.infoBox}>
        <h3 className={styles.infoTitle}>施設情報</h3>
        <div className={styles.infoBody}>
          {hotel.roomImage && (
            <div className={styles.roomImageWrap}>
              <img
                className={styles.roomImage}
                src={hotel.roomImage}
                alt={hotel.name + 'の客室・施設画像'}
                width="100"
                height="100"
                loading="lazy"
                decoding="async"
              />
            </div>
          )}
          <div className={styles.infoTableWrap}>
            <table className={styles.infoTable}>
              <tbody>
                {hotel.address && (
                  <tr>
                    <th>住所</th>
                    <td>{hotel.address}</td>
                  </tr>
                )}
                {hotel.tel && (
                  <tr>
                    <th>電話</th>
                    <td>{hotel.tel}</td>
                  </tr>
                )}
                {hotel.access && (
                  <tr>
                    <th>アクセス</th>
                    <td>{hotel.access}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className={styles.linkGrid}>
          {linkOrder.map((key) => {
            const link = links[key]
            if (!link || (!link.url && !link.couponUrl)) return null

            return <HotelLink key={key} linkKey={key} link={link} />
          })}
        </div>
      </div>
    </section>
  )
}

export default HotelCard
